
import { ref, defineComponent, onMounted, computed } from "vue";
import {
  Course,
  TrainingDocument,
  TrainingDetailDto,
  TrainingAttendance,
  UpsertTrainingDocumentRequest,
  CancelTrainingDocumentRequest,
  DownloadTrainingAttendanceRequest,
  TrainingTypeEnum,
} from "@/types";
import { usePlugin } from "@/plugins";
import TrainingAttendanceList from "../../components/TrainingAttendanceList.vue";
import InputInstructor from "@/components/InputInstructor.vue";
import InputUser from "@/components/InputUser.vue";
import InputQuestion from "@/components/InputQuestion.vue";
import BackButton from "@/components/BackButton.vue";
import CancelButton from "@/components/CancelButton.vue";
import CompleteButton from "@/components/CompleteButton.vue";
import StatusBadge from "@/components/StatusBadge.vue";
import { useQRCode } from "@vueuse/integrations";
import { useConfirm } from "primevue/useconfirm";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { saveAs } from "file-saver";

export default defineComponent({
  name: "TrainingDocumentMaintain",
  components: {
    TrainingAttendanceList,
    InputInstructor,
    InputQuestion,
    BackButton,
    CancelButton,
    StatusBadge,
    InputUser,
    CompleteButton,
  },
  setup() {
    const {
      success,
      error,
      axios,
      setLoading,
      route,
      trainingTypeDesc,
      formatDate,
      dayjs,
      formatAppDateTime,
      useState,
      isAdmin,
    } = usePlugin();
    const confirm = useConfirm();
    const course = ref<Course>({} as Course);
    const trainingDocument = ref<TrainingDocument>({} as TrainingDocument);
    const trainingAttendances = ref<TrainingAttendance[]>([]);
    const selectedDate = ref<Date[]>([dayjs().toDate(), dayjs().toDate()]);

    const newRules = {
      trainingDocument: {
        trainingType: { required },
        courseId: { required },
        location: { required },
        startDate: { required },
        endDate: { required },
        time: { required },
        hours: { required },
        dunning1: { required },
        dunning2: { required },
      },
    };
    const v$ = useVuelidate(newRules, { trainingDocument });

    const loadTrainingDocument = async () => {
      if (route.params.docNo) {
        setLoading(true);
        try {
          const result = await axios.get<TrainingDetailDto>(
            `api/v1/services/trainingDocument/${route.params.docNo}`
          );
          trainingDocument.value =
            result.data.trainingDocument ?? ({} as TrainingDocument);
          trainingDocument.value.startDate = dayjs(
            trainingDocument.value.startDate
          ).toDate();
          trainingDocument.value.endDate = dayjs(
            trainingDocument.value.endDate
          ).toDate();

          course.value = result.data.trainingDocument?.course ?? ({} as Course);
          trainingAttendances.value = result.data.trainingAttendances ?? [];

          selectedDate.value = [
            result.data.trainingDocument?.startDate ?? dayjs().toDate(),
            result.data.trainingDocument?.endDate ?? dayjs().toDate(),
          ];
          // await prepTrainingAttendance();
        } catch (e) {
          error(e);
        } finally {
          setLoading(false);
        }
      }
    };
    // const prepTrainingAttendance = async () => {
    //   if (trainingAttendances.value) {
    //     for (let i = 0; i < trainingAttendances.value.length; i++) {
    //       const trainingAttendance = trainingAttendances.value[i];

    //       const user = await searchUser(trainingAttendance?.employeeId);
    //       if (user && user.length > 0) {
    //         const trainingAttendanceDto = {
    //           trainingDocumentId: trainingAttendance.trainingDocumentId,
    //           employeeId: trainingAttendance.employeeId,
    //           internalAssessmentData: trainingAttendance.internalAssessmentData,
    //           externalAssessmentData: trainingAttendance.externalAssessmentData,
    //           knowledgeAssessmentData:
    //             trainingAttendance.knowledgeAssessmentData,
    //           User: {
    //             ...user[0],
    //             fullName: `${user[0].prename} ${user[0].name} ${user[0].surname}`,
    //           },
    //         };
    //         trainingAttendancesDto.value.push(trainingAttendanceDto);
    //       }
    //     }
    //   }
    //   syncData.value = true;
    // };

    const onReleaseTrainingDocument = () => {
      confirm.require({
        header: "เริ่มใช้งาน",
        message: "ยืนยันเริ่มใช้งานเอกสาร Training",
        acceptLabel: "ยืนยันใช้งาน",
        accept: async () => {
          await onSaveTrainingDocument(true);
        },
        rejectLabel: "ยกเลิก",
      });
    };
    const state = useState();
    const onCancelDocument = async () => {
      setLoading(true);
      try {
        await axios.post("api/v1/services/trainingDocument/cancel", {
          trainingDocumentId: trainingDocument.value.id,
        } as CancelTrainingDocumentRequest);
        success("ยกเลิกเอกสารเรียบร้อยแล้ว");
        await loadTrainingDocument();
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };
    const onCompleteDocument = async () => {
      setLoading(true);
      try {
        await axios.post("api/v1/services/trainingDocument/complete", {
          trainingDocumentId: trainingDocument.value.id,
        } as CancelTrainingDocumentRequest);
        success("ยืนยันสิ้นสุดกระบวนการเรียบร้อยแล้ว");
        await loadTrainingDocument();
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };
    const onSaveTrainingDocument = async (submit = false) => {
      v$.value.$reset();
      v$.value.$touch();
      if (!(await v$.value.$validate())) return;

      setLoading(true);
      const trainingDocumentUpdate = {
        id: trainingDocument.value.id,
        courseId: trainingDocument.value.courseId,
        startDate: selectedDate.value[0],
        endDate: selectedDate.value[1],
        location: trainingDocument.value.location,
        course: trainingDocument.value.course,
        trainingType: trainingDocument.value.trainingType,
        instructorId1: trainingDocument.value.instructorId1,
        instructorId2: trainingDocument.value.instructorId2,
        instructorId3: trainingDocument.value.instructorId3,
        internalInstructorId1: trainingDocument.value.internalInstructorId1,
        internalInstructorId2: trainingDocument.value.internalInstructorId2,
        internalInstructorId3: trainingDocument.value.internalInstructorId3,
        instructorQuestionId: trainingDocument.value.instructorQuestionId,
        satisfactionQuestionId: trainingDocument.value.satisfactionQuestionId,
        knowledgeQuestionId: trainingDocument.value.knowledgeQuestionId,
        instructorWaitingPeriod: trainingDocument.value.instructorWaitingPeriod,
        satisfactionWaitingPeriod:
          trainingDocument.value.satisfactionWaitingPeriod,
        knowledgeWaitingPeriod: trainingDocument.value.knowledgeWaitingPeriod,
        dunning1: trainingDocument.value.dunning1,
        dunning2: trainingDocument.value.dunning2,
        hours: trainingDocument.value.hours,
        time: trainingDocument.value.time,
        remark: trainingDocument.value.remark,
      } as TrainingDocument;

      if (submit === true) {
        trainingDocumentUpdate.releaseOn = dayjs().toDate();
        trainingDocumentUpdate.releaseById =
          state.value.authentication.username;
      }

      try {
        const result = await axios.post(
          "api/v1/services/trainingDocument/upsert",
          {
            trainingDocuments: [trainingDocumentUpdate],
          } as UpsertTrainingDocumentRequest
        );
        success("บันทึกข้อมูลสำเร็จแล้ว");
        // emit("newTrainingDocument");
        await loadTrainingDocument();
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const showQrPopup = ref(false);
    const qrCodeRaw = computed(
      () =>
        `${window.origin}/#/training/assessment/${trainingDocument.value.id}`
    );
    const qrCode = useQRCode(qrCodeRaw);
    const onShowQrCode = () => {
      showQrPopup.value = true;
    };
    const isReadonly = computed(() => {
      return (
        trainingDocument.value.releaseOn !== null ||
        trainingDocument.value.cancelOn !== null
      );
    });

    const onDownloadAttendanceList = async () => {
      setLoading(true);
      try {
        const result = await axios.post(
          "api/v1/services/trainingAttendance/download",
          {
            id: trainingDocument.value.id,
          } as DownloadTrainingAttendanceRequest,
          { responseType: "blob" }
        );
        saveAs(
          result.data,
          `ATT_${trainingDocument.value.id}_${dayjs().format(
            "YYYY-MM-DD_HHmmss"
          )}.xlsx`
        );
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    onMounted(async () => {
      await loadTrainingDocument();
    });

    return {
      trainingDocument,
      selectedDate,
      trainingAttendances,
      course,
      trainingTypeDesc,
      formatDate,
      onSaveTrainingDocument,
      onReleaseTrainingDocument,
      onDownloadAttendanceList,
      onCancelDocument,
      onCompleteDocument,

      onShowQrCode,
      showQrPopup,
      qrCode,
      qrCodeRaw,

      formatAppDateTime,
      isReadonly,
      isAdmin,

      v$,

      TrainingTypeEnum,
    };
  },
});
